import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

// Tokens are kept in sessionStorage (cleared when the tab closes) to reduce XSS exposure.
// SECURITY NOTE: sessionStorage is JS-readable, so XSS in any included script can steal the token.
// For higher-security deployments consider httpOnly cookies + CSRF protection — accepted risk for MVP.
const TOKEN_KEY = "pb_token";
const USER_KEY = "pb_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      sessionStorage.setItem(USER_KEY, JSON.stringify(data));
    } catch (err) {
      // Token invalid/expired — clear session and force re-login.
      console.warn("auth refresh failed:", err?.response?.status || err?.message);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Even if the server logout fails, we still clear the local session.
      console.warn("logout request failed:", err?.message);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  // Memoize the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
